import { DbEnum } from "../../../generated/prisma-enums";

/**
 * A COLLECTION skill must carry usage instructions — the text that tells the
 * agent WHEN to search the knowledge base.
 *
 * Without them the provider-facing description falls through to a generic
 * sentence built from the label alone, and the model then routinely answers
 * from its own knowledge instead of searching. Measured on staging with
 * interleaved arms: the generic sentence was declined 15 times in 20, real
 * instructions 1 time in 20.
 *
 * One rule, read by every door that writes a COLLECTION config, so "blank"
 * cannot mean one thing at the contract and another at a controller.
 *
 * Existing rows are not judged here. A row stored before this rule still
 * reads, runs and falls back as before; only a write that SUPPLIES a
 * COLLECTION config is refused when that config names no instructions.
 */

/**
 * The type's VALUES rather than the enum, so the backend's Prisma string union
 * and the contract's `DbEnum` member both fit without a cast.
 */
type AgentToolConfigTypeValue = `${DbEnum.AgentToolConfigType}`;

const COLLECTION: AgentToolConfigTypeValue = DbEnum.AgentToolConfigType.COLLECTION;

/** The refusal every door answers with, so a client sees one sentence. */
export const COLLECTION_TOOL_INSTRUCTIONS_REQUIRED_MESSAGE =
  "COLLECTION config requires non-empty `instructions`: tell the agent when to search this knowledge base";

/**
 * Whether `value` carries no readable text.
 *
 * Tags are removed before judging because the skill editor is rich text: an
 * emptied editor posts `<p></p>`, which is not an empty string and is still
 * nothing to show the model. The non-breaking-space entities are the ones an
 * emptied editor emits; any other entity decodes to a visible character.
 */
export function isBlankInstructionText(value: unknown): boolean {
  if (typeof value !== "string") return true;
  const visible = value.replace(/<[^>]*>/g, " ").replace(/&(nbsp|#160|#xa0);/gi, " ");
  return visible.trim().length === 0;
}

/**
 * Whether a COLLECTION config being WRITTEN lacks instructions.
 *
 * `type` is the config's own type; any other type is outside this rule and
 * answers `false`, so a WORKFLOW or PLUGIN skill with no instructions is
 * untouched.
 */
export function collectionConfigLacksInstructions(
  type: AgentToolConfigTypeValue,
  config: unknown
): boolean {
  if (type !== COLLECTION) return false;
  if (typeof config !== "object" || config === null || Array.isArray(config)) return true;
  return isBlankInstructionText("instructions" in config ? config.instructions : undefined);
}
